import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, Mail, Phone, MapPin, FileText, FolderKanban,
  DollarSign, Paperclip, Users, Plus, Edit, Calendar, Clock, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import api from '@/utils/api';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/utils/helpers';

const STATUS_CONFIG = {
  ativo: { label: 'Ativo', color: 'bg-green-100 text-green-700' },
  inativo: { label: 'Inativo', color: 'bg-slate-100 text-slate-700' },
  potencial: { label: 'Potencial', color: 'bg-blue-100 text-blue-700' },
  churned: { label: 'Churned', color: 'bg-red-100 text-red-700' },
};

const CONTRACT_STATUS = {
  ativo: { label: 'Ativo', color: 'bg-green-100 text-green-700' },
  pausado: { label: 'Pausado', color: 'bg-yellow-100 text-yellow-700' },
  encerrado: { label: 'Encerrado', color: 'bg-slate-100 text-slate-700' },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
};

const PROJECT_STATUS = {
  backlog: { label: 'Backlog', color: 'bg-slate-100 text-slate-700' },
  em_andamento: { label: 'Em Andamento', color: 'bg-blue-100 text-blue-700' },
  revisao: { label: 'Revisão', color: 'bg-yellow-100 text-yellow-700' },
  entregue: { label: 'Entregue', color: 'bg-green-100 text-green-700' },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
};

const PAYMENT_STATUS = {
  previsto: { label: 'Previsto', color: 'bg-slate-100 text-slate-700' },
  em_aberto: { label: 'Em Aberto', color: 'bg-blue-100 text-blue-700' },
  pago: { label: 'Pago', color: 'bg-green-100 text-green-700' },
  atrasado: { label: 'Atrasado', color: 'bg-red-100 text-red-700' },
  cancelado: { label: 'Cancelado', color: 'bg-slate-100 text-slate-700' },
};

export default function ClienteDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [summary, setSummary] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [isEditClientOpen, setIsEditClientOpen] = useState(false);
  const [isNewContractOpen, setIsNewContractOpen] = useState(false);
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Edit client form
  const [editClientForm, setEditClientForm] = useState({
    name: '',
    status: 'ativo',
    contact: { email_principal: '', phone_principal: '', whatsapp: '' },
    address: { city: '', state: '' },
    segmento: '',
    origem: '',
    notes: ''
  });

  // New contract form
  const [newContractForm, setNewContractForm] = useState({
    title: '',
    contract_type: 'outro',
    valor_recorrente: '',
    billing_frequency: 'mensal',
    start_date: '',
    duration_months: '12'
  });

  // New project form
  const [newProjectForm, setNewProjectForm] = useState({
    name: '',
    description: '',
    project_type: 'outro',
    priority: 'media',
    start_date: '',
    deadline: '',
    orcamento: ''
  });

  useEffect(() => {
    loadClientData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadClientData = async () => {
    setLoading(true);
    try {
      const [clientRes, summaryRes, contractsRes, projectsRes, paymentsRes] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/summary`),
        api.get(`/contracts?client_id=${id}`),
        api.get(`/projects?client_id=${id}`),
        api.get(`/payments?client_id=${id}`)
      ]);

      setClient(clientRes.data);
      setSummary(summaryRes.data);
      setContracts(contractsRes.data);
      setProjects(projectsRes.data);
      setPayments(paymentsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados do cliente');
      navigate('/clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async (paymentId) => {
    try {
      await api.put(`/payments/${paymentId}/pay`);
      toast.success('Pagamento marcado como pago!');
      loadClientData();
    } catch (error) {
      toast.error('Erro ao marcar pagamento');
    }
  };

  // Handle Edit Client
  const openEditClientModal = () => {
    setEditClientForm({
      name: client.name || '',
      status: client.status || 'ativo',
      contact: {
        email_principal: client.contact?.email_principal || '',
        phone_principal: client.contact?.phone_principal || '',
        whatsapp: client.contact?.whatsapp || ''
      },
      address: {
        city: client.address?.city || '',
        state: client.address?.state || ''
      },
      segmento: client.segmento || '',
      origem: client.origem || '',
      notes: client.notes || ''
    });
    setIsEditClientOpen(true);
  };

  const handleEditClient = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await api.put(`/clients/${id}`, editClientForm);
      toast.success('Cliente atualizado com sucesso!');
      setIsEditClientOpen(false);
      loadClientData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erro ao atualizar cliente');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle New Contract
  const handleNewContract = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = {
        client_id: id,
        title: newContractForm.title,
        contract_type: newContractForm.contract_type,
        valor_recorrente: parseFloat(newContractForm.valor_recorrente) || 0,
        billing_frequency: newContractForm.billing_frequency,
        start_date: new Date(newContractForm.start_date).toISOString(),
        duration_months: parseInt(newContractForm.duration_months) || null
      };
      await api.post('/contracts', payload);
      toast.success('Contrato criado com sucesso!');
      setIsNewContractOpen(false);
      setNewContractForm({
        title: '', contract_type: 'outro', valor_recorrente: '',
        billing_frequency: 'mensal', start_date: '', duration_months: '12'
      });
      loadClientData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erro ao criar contrato');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle New Project
  const handleNewProject = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = {
        client_id: id,
        name: newProjectForm.name,
        description: newProjectForm.description,
        project_type: newProjectForm.project_type,
        priority: newProjectForm.priority,
        start_date: newProjectForm.start_date ? new Date(newProjectForm.start_date).toISOString() : null,
        deadline: newProjectForm.deadline ? new Date(newProjectForm.deadline).toISOString() : null,
        orcamento: parseFloat(newProjectForm.orcamento) || 0
      };
      await api.post('/projects', payload);
      toast.success('Projeto criado com sucesso!');
      setIsNewProjectOpen(false);
      setNewProjectForm({
        name: '', description: '', project_type: 'outro',
        priority: 'media', start_date: '', deadline: '', orcamento: ''
      });
      loadClientData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erro ao criar projeto');
    } finally {
      setFormLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="space-y-6" data-testid="cliente-detalhe-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/clientes')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-heading font-bold text-slate-900">{client.name}</h1>
            <Badge className={STATUS_CONFIG[client.status]?.color}>
              {STATUS_CONFIG[client.status]?.label}
            </Badge>
          </div>
          {client.juridico?.nome_fantasia && (
            <p className="text-slate-500 mt-1">{client.juridico.nome_fantasia}</p>
          )}
        </div>
        <Button variant="outline" onClick={openEditClientModal} data-testid="edit-client-btn">
          <Edit className="h-4 w-4 mr-2" /> Editar
        </Button>
      </div>

      {/* Edit Client Modal */}
      <Dialog open={isEditClientOpen} onOpenChange={setIsEditClientOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
            <DialogDescription>Atualize as informações do cliente</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditClient} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome *</Label>
                <Input
                  value={editClientForm.name}
                  onChange={(e) => setEditClientForm({ ...editClientForm, name: e.target.value })}
                  required
                  data-testid="edit-client-name"
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editClientForm.status} onValueChange={(v) => setEditClientForm({ ...editClientForm, status: v })}>
                  <SelectTrigger data-testid="edit-client-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                    <SelectItem value="potencial">Potencial</SelectItem>
                    <SelectItem value="churned">Churned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editClientForm.contact.email_principal}
                  onChange={(e) => setEditClientForm({
                    ...editClientForm,
                    contact: { ...editClientForm.contact, email_principal: e.target.value }
                  })}
                  data-testid="edit-client-email"
                />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input
                  value={editClientForm.contact.phone_principal}
                  onChange={(e) => setEditClientForm({
                    ...editClientForm,
                    contact: { ...editClientForm.contact, phone_principal: e.target.value }
                  })}
                  data-testid="edit-client-phone"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Segmento</Label>
                <Input
                  value={editClientForm.segmento}
                  onChange={(e) => setEditClientForm({ ...editClientForm, segmento: e.target.value })}
                  data-testid="edit-client-segmento"
                />
              </div>
              <div>
                <Label>Origem</Label>
                <Input
                  value={editClientForm.origem}
                  onChange={(e) => setEditClientForm({ ...editClientForm, origem: e.target.value })}
                  data-testid="edit-client-origem"
                />
              </div>
            </div>
            <div>
              <Label>Observações</Label>
              <textarea
                className="w-full min-h-[80px] px-3 py-2 text-sm rounded-md border border-input bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                value={editClientForm.notes}
                onChange={(e) => setEditClientForm({ ...editClientForm, notes: e.target.value })}
                data-testid="edit-client-notes"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setIsEditClientOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={formLoading} data-testid="submit-edit-client">
                {formLoading ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Contract Modal */}
      <Dialog open={isNewContractOpen} onOpenChange={setIsNewContractOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Novo Contrato</DialogTitle>
            <DialogDescription>Adicione um novo contrato para {client?.name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleNewContract} className="space-y-4">
            <div>
              <Label>Título do Contrato *</Label>
              <Input
                value={newContractForm.title}
                onChange={(e) => setNewContractForm({ ...newContractForm, title: e.target.value })}
                required
                data-testid="new-contract-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Contrato</Label>
                <Select value={newContractForm.contract_type} onValueChange={(v) => setNewContractForm({ ...newContractForm, contract_type: v })}>
                  <SelectTrigger data-testid="new-contract-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="social_media">Social Media</SelectItem>
                    <SelectItem value="branding">Branding</SelectItem>
                    <SelectItem value="marketing_digital">Marketing Digital</SelectItem>
                    <SelectItem value="consultoria">Consultoria</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Frequência de Cobrança</Label>
                <Select value={newContractForm.billing_frequency} onValueChange={(v) => setNewContractForm({ ...newContractForm, billing_frequency: v })}>
                  <SelectTrigger data-testid="new-contract-billing">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensal">Mensal</SelectItem>
                    <SelectItem value="trimestral">Trimestral</SelectItem>
                    <SelectItem value="semestral">Semestral</SelectItem>
                    <SelectItem value="anual">Anual</SelectItem>
                    <SelectItem value="avulso">Avulso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Valor Recorrente (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newContractForm.valor_recorrente}
                  onChange={(e) => setNewContractForm({ ...newContractForm, valor_recorrente: e.target.value })}
                  data-testid="new-contract-value"
                />
              </div>
              <div>
                <Label>Duração (meses)</Label>
                <Input
                  type="number"
                  value={newContractForm.duration_months}
                  onChange={(e) => setNewContractForm({ ...newContractForm, duration_months: e.target.value })}
                  data-testid="new-contract-duration"
                />
              </div>
            </div>
            <div>
              <Label>Data de Início *</Label>
              <Input
                type="date"
                value={newContractForm.start_date}
                onChange={(e) => setNewContractForm({ ...newContractForm, start_date: e.target.value })}
                required
                data-testid="new-contract-start-date"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setIsNewContractOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={formLoading} data-testid="submit-new-contract">
                {formLoading ? 'Salvando...' : 'Criar Contrato'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Project Modal */}
      <Dialog open={isNewProjectOpen} onOpenChange={setIsNewProjectOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Novo Projeto</DialogTitle>
            <DialogDescription>Adicione um novo projeto para {client?.name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleNewProject} className="space-y-4">
            <div>
              <Label>Nome do Projeto *</Label>
              <Input
                value={newProjectForm.name}
                onChange={(e) => setNewProjectForm({ ...newProjectForm, name: e.target.value })}
                required
                data-testid="new-project-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo</Label>
                <Select value={newProjectForm.project_type} onValueChange={(v) => setNewProjectForm({ ...newProjectForm, project_type: v })}>
                  <SelectTrigger data-testid="new-project-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="social_media">Social Media</SelectItem>
                    <SelectItem value="branding">Branding</SelectItem>
                    <SelectItem value="marketing_digital">Marketing Digital</SelectItem>
                    <SelectItem value="consultoria">Consultoria</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prioridade</Label>
                <Select value={newProjectForm.priority} onValueChange={(v) => setNewProjectForm({ ...newProjectForm, priority: v })}>
                  <SelectTrigger data-testid="new-project-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Média</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Início</Label>
                <Input
                  type="date"
                  value={newProjectForm.start_date}
                  onChange={(e) => setNewProjectForm({ ...newProjectForm, start_date: e.target.value })}
                  data-testid="new-project-start-date"
                />
              </div>
              <div>
                <Label>Prazo Final</Label>
                <Input
                  type="date"
                  value={newProjectForm.deadline}
                  onChange={(e) => setNewProjectForm({ ...newProjectForm, deadline: e.target.value })}
                  data-testid="new-project-deadline"
                />
              </div>
            </div>
            <div>
              <Label>Orçamento (R$)</Label>
              <Input
                type="number"
                step="0.01"
                value={newProjectForm.orcamento}
                onChange={(e) => setNewProjectForm({ ...newProjectForm, orcamento: e.target.value })}
                data-testid="new-project-orcamento"
              />
            </div>
            <div>
              <Label>Descrição</Label>
              <textarea
                className="w-full min-h-[80px] px-3 py-2 text-sm rounded-md border border-input bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                value={newProjectForm.description}
                onChange={(e) => setNewProjectForm({ ...newProjectForm, description: e.target.value })}
                data-testid="new-project-description"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setIsNewProjectOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={formLoading} data-testid="submit-new-project">
                {formLoading ? 'Salvando...' : 'Criar Projeto'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* KPIs do Cliente */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <DollarSign className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Receita Mensal</p>
              <p className="text-xl font-bold text-slate-900">{formatCurrency(summary?.receita_mensal_ativa || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Contratos Ativos</p>
              <p className="text-xl font-bold text-slate-900">{summary?.contratos_ativos || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
              <FolderKanban className="h-5 w-5 text-accent" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Projetos Ativos</p>
              <p className="text-xl font-bold text-slate-900">{summary?.projetos_em_andamento || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Total Recebido</p>
              <p className="text-xl font-bold text-success">{formatCurrency(summary?.total_recebido || 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Abas */}
      <Tabs defaultValue="resumo" className="w-full">
        <TabsList className="bg-white border border-slate-100 p-1">
          <TabsTrigger value="resumo">Resumo</TabsTrigger>
          <TabsTrigger value="contratos">Contratos ({contracts.length})</TabsTrigger>
          <TabsTrigger value="projetos">Projetos ({projects.length})</TabsTrigger>
          <TabsTrigger value="financeiro">Financeiro</TabsTrigger>
          <TabsTrigger value="dados">Dados Cadastrais</TabsTrigger>
        </TabsList>

        {/* Resumo */}
        <TabsContent value="resumo" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Info Rápida */}
            <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5" /> Informações
              </h3>
              <div className="space-y-3 text-sm">
                {client.contact?.email_principal && (
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-slate-400" />
                    <span>{client.contact.email_principal}</span>
                  </div>
                )}
                {client.contact?.phone_principal && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-slate-400" />
                    <span>{client.contact.phone_principal}</span>
                  </div>
                )}
                {client.address?.city && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    <span>{client.address.city}, {client.address.state}</span>
                  </div>
                )}
                {client.segmento && (
                  <div className="pt-2 mt-2 border-t border-slate-100">
                    <span className="text-slate-500">Segmento:</span> {client.segmento}
                  </div>
                )}
                {client.origem && (
                  <div>
                    <span className="text-slate-500">Origem:</span> {client.origem}
                  </div>
                )}
              </div>
            </div>

            {/* Contratos Recentes */}
            <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="h-5 w-5" /> Contratos
                </h3>
                <Button variant="ghost" size="sm" onClick={() => navigate(`/contratos?client_id=${id}`)}>
                  Ver todos
                </Button>
              </div>
              <div className="space-y-3">
                {contracts.slice(0, 3).map((contract) => (
                  <div key={contract.id} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{contract.title}</span>
                      <Badge className={CONTRACT_STATUS[contract.status]?.color} variant="secondary">
                        {CONTRACT_STATUS[contract.status]?.label}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatCurrency(contract.valor_recorrente)}/mês
                    </p>
                  </div>
                ))}
                {contracts.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">Nenhum contrato</p>
                )}
              </div>
            </div>

            {/* Projetos Recentes */}
            <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <FolderKanban className="h-5 w-5" /> Projetos
                </h3>
                <Button variant="ghost" size="sm" onClick={() => navigate(`/projetos?client_id=${id}`)}>
                  Ver todos
                </Button>
              </div>
              <div className="space-y-3">
                {projects.slice(0, 3).map((project) => (
                  <div key={project.id} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{project.name}</span>
                      <Badge className={PROJECT_STATUS[project.status]?.color} variant="secondary">
                        {PROJECT_STATUS[project.status]?.label}
                      </Badge>
                    </div>
                    {project.deadline && (
                      <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Prazo: {formatDate(project.deadline)}
                      </p>
                    )}
                  </div>
                ))}
                {projects.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">Nenhum projeto</p>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Contratos */}
        <TabsContent value="contratos" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-semibold">Contratos do Cliente</h3>
              <Button size="sm" onClick={() => setIsNewContractOpen(true)} data-testid="new-contract-btn">
                <Plus className="h-4 w-4 mr-2" /> Novo Contrato
              </Button>
            </div>
            <div className="divide-y divide-slate-100">
              {contracts.map((contract) => (
                <div key={contract.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-slate-900">{contract.title}</h4>
                        <Badge className={CONTRACT_STATUS[contract.status]?.color}>
                          {CONTRACT_STATUS[contract.status]?.label}
                        </Badge>
                        {contract.is_signed && (
                          <Badge variant="outline" className="text-green-600 border-green-200">
                            Assinado
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        {contract.contract_number} • {contract.contract_type}
                      </p>
                      {contract.escopo_resumido && (
                        <p className="text-sm text-slate-600 mt-2">{contract.escopo_resumido}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-primary">{formatCurrency(contract.valor_recorrente)}<span className="text-sm font-normal text-slate-500">/mês</span></p>
                      <p className="text-xs text-slate-500 mt-1">
                        {formatDate(contract.start_date)} - {contract.end_date ? formatDate(contract.end_date) : 'Indeterminado'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {contracts.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  Nenhum contrato cadastrado para este cliente
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Projetos */}
        <TabsContent value="projetos" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-semibold">Projetos do Cliente</h3>
              <Button size="sm" onClick={() => setIsNewProjectOpen(true)} data-testid="new-project-btn">
                <Plus className="h-4 w-4 mr-2" /> Novo Projeto
              </Button>
            </div>
            <div className="divide-y divide-slate-100">
              {projects.map((project) => (
                <div key={project.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-slate-900">{project.name}</h4>
                        <Badge className={PROJECT_STATUS[project.status]?.color}>
                          {PROJECT_STATUS[project.status]?.label}
                        </Badge>
                      </div>
                      {project.description && (
                        <p className="text-sm text-slate-600 mt-1">{project.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        {project.deadline && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> Prazo: {formatDate(project.deadline)}
                          </span>
                        )}
                        {project.horas_estimadas > 0 && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" /> {project.horas_reais}h / {project.horas_estimadas}h
                          </span>
                        )}
                      </div>
                      {project.horas_estimadas > 0 && (
                        <div className="mt-3">
                          <Progress value={(project.horas_reais / project.horas_estimadas) * 100} className="h-2" />
                        </div>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-lg font-bold text-slate-900">{formatCurrency(project.orcamento)}</p>
                      <p className="text-xs text-slate-500">Orçamento</p>
                    </div>
                  </div>
                </div>
              ))}
              {projects.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  Nenhum projeto cadastrado para este cliente
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Financeiro */}
        <TabsContent value="financeiro" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="bg-green-50 p-5 rounded-xl border border-green-100">
              <p className="text-sm text-green-700 uppercase font-medium">Total Recebido</p>
              <p className="text-2xl font-bold text-green-700">{formatCurrency(summary?.total_recebido || 0)}</p>
            </div>
            <div className="bg-yellow-50 p-5 rounded-xl border border-yellow-100">
              <p className="text-sm text-yellow-700 uppercase font-medium">Pendente</p>
              <p className="text-2xl font-bold text-yellow-700">{formatCurrency(summary?.total_pendente || 0)}</p>
            </div>
            <div className="bg-primary/5 p-5 rounded-xl border border-primary/10">
              <p className="text-sm text-primary uppercase font-medium">Receita Mensal</p>
              <p className="text-2xl font-bold text-primary">{formatCurrency(summary?.receita_mensal_ativa || 0)}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
            <div className="p-4 border-b border-slate-100">
              <h3 className="font-semibold">Histórico de Pagamentos</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50/50 border-b border-slate-100">
                  <tr>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Descrição</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Vencimento</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Valor</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="h-12 px-4 text-left text-xs font-medium text-slate-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="p-4 text-sm">{payment.description || 'Mensalidade'}</td>
                      <td className="p-4 text-sm">{formatDate(payment.due_date)}</td>
                      <td className="p-4 text-sm font-medium">{formatCurrency(payment.value)}</td>
                      <td className="p-4">
                        <Badge className={PAYMENT_STATUS[payment.status]?.color}>
                          {PAYMENT_STATUS[payment.status]?.label}
                        </Badge>
                      </td>
                      <td className="p-4">
                        {payment.status !== 'pago' && payment.status !== 'cancelado' && (
                          <Button variant="ghost" size="sm" onClick={() => handleMarkPaid(payment.id)}>
                            Marcar Pago
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {payments.length === 0 && (
                    <tr>
                      <td colSpan="5" className="p-8 text-center text-slate-500">
                        Nenhum pagamento registrado
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Dados Cadastrais */}
        <TabsContent value="dados" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Dados Jurídicos */}
            <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4">Dados Jurídicos</h3>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Razão Social:</span>
                  <span className="font-medium">{client.juridico?.razao_social || '-'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Nome Fantasia:</span>
                  <span className="font-medium">{client.juridico?.nome_fantasia || '-'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">CNPJ:</span>
                  <span className="font-medium">{client.juridico?.cnpj || '-'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Inscrição Estadual:</span>
                  <span className="font-medium">{client.juridico?.inscricao_estadual || '-'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Regime Tributário:</span>
                  <span className="font-medium">{client.juridico?.tax_regime || '-'}</span>
                </div>
              </div>
            </div>

            {/* Endereço */}
            <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4">Endereço</h3>
              <div className="space-y-3 text-sm">
                {client.address?.street ? (
                  <>
                    <p>{client.address.street}, {client.address.number}</p>
                    {client.address.complement && <p>{client.address.complement}</p>}
                    <p>{client.address.neighborhood}</p>
                    <p>{client.address.city} - {client.address.state}</p>
                    <p>CEP: {client.address.zipcode}</p>
                  </>
                ) : (
                  <p className="text-slate-500">Endereço não cadastrado</p>
                )}
              </div>
            </div>

            {/* Contatos */}
            <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4">Contatos</h3>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Email Principal:</span>
                  <span className="font-medium">{client.contact?.email_principal || '-'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Email Financeiro:</span>
                  <span className="font-medium">{client.contact?.email_financeiro || '-'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Telefone:</span>
                  <span className="font-medium">{client.contact?.phone_principal || '-'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">WhatsApp:</span>
                  <span className="font-medium">{client.contact?.whatsapp || '-'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Website:</span>
                  <span className="font-medium">{client.contact?.website || '-'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Instagram:</span>
                  <span className="font-medium">{client.contact?.instagram || '-'}</span>
                </div>
              </div>
            </div>

            {/* Financeiro */}
            <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4">Dados Financeiros</h3>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Dia Vencimento:</span>
                  <span className="font-medium">{client.financeiro?.dia_vencimento || 10}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Forma de Pagamento:</span>
                  <span className="font-medium uppercase">{client.financeiro?.payment_method || '-'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Ticket Médio:</span>
                  <span className="font-medium">{client.financeiro?.ticket_medio ? formatCurrency(client.financeiro.ticket_medio) : '-'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Observações */}
          {client.notes && (
            <div className="mt-6 bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4">Observações</h3>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{client.notes}</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
